import {useIsUserAuthenticated} from '@/app/signup/(state)/UserProfileState';
import {
  googleProxyPrefix,
  isRequestedFromGoogleProxy,
} from '@gfiber/pixel-party';
import {isOnServer} from '@gfiber/typesmith';
import {usePathname, useRouter, useSearchParams} from 'next/navigation';
import {useCallback} from 'react';
import {proxy} from 'valtio';
import {
  addToHistory,
  store as historyStore,
  popFromHistory,
} from '../LocalHistoryState';
import {
  AUTH_REQUIRED_PAGES,
  StepsPath,
  getPagePrefix,
  getStepPathFromRequestPath,
} from '../StepsRouting';
import {buyflowStore, resetPersistBuyflowState} from '../buyflowStore';

/**
 * Will redirect user to target page at the end of buyflow.
 * Url of the target page is return from BFF.
 * */
export const useRedirectForEndBuyflow = () => {
  const router = useRouter();

  return useCallback(
    (redirectUrl: string) => {
      let targetUrl = redirectUrl;
      // For related URL and request from google.com
      if (redirectUrl.startsWith('/') && isRequestedFromGoogleProxy()) {
        targetUrl = googleProxyPrefix + redirectUrl;
      }
      resetPersistBuyflowState();
      router.push(targetUrl);
    },
    [router],
  );
};

interface BuyflowEnded {
  hasBuyflowEnded: boolean;
}

/**
 * Local storage is cleared at the end of buyflow, which triggers rerendering
 * due to state change.  Set this state to true so that rerendering is stopped
 * which avoids interference with redirecting to Portal.
 */
export const buyflowEnded = proxy<BuyflowEnded>({hasBuyflowEnded: false});

export interface RedirectOptions {
  /** Will reload page to redirect target */
  refresh?: boolean;
  params?: URLSearchParams;
}

// TODO: migrate to dynamic routing to remove this hack
/** Redirect to next step and replace url in history */
export const useSignupRedirect = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentGlobalAddressId = searchParams?.get('global_addr_id');
  const currentStepPath = getStepPathFromRequestPath(pathname);
  const isUserAuthenticated = useIsUserAuthenticated();

  return useCallback(() => {
    // stored information can be updated during page loading
    // initialize here to keep useCallback return not change
    const storedStepsPath = historyStore.pages;
    const storedLastStepPath = historyStore.last;
    const storedGlobalAddressId =
      buyflowStore.state?.serviceAddressInfo?.globalAddressId;

    if (isOnServer()) {
      return false;
    }

    if (buyflowEnded.hasBuyflowEnded) {
      return true;
    }

    let targetStepPath = '';

    // check if global address id is passed in
    if (currentGlobalAddressId) {
      // global address id is passed in
      // find the right step name and its path
      // check if visited before by comparing current global address with stored global address
      if (currentGlobalAddressId === storedGlobalAddressId) {
        // TODO: add logic for special steps
        // visited before (go to next step always push the next step in the history)
        // check if path is for a valid step
        if (Object.values(StepsPath).includes(currentStepPath as StepsPath)) {
          // the path is a valid step
          // check if the step has been visited before
          // special case to align with current Buyflow
          // if it is the root then redirect to the last visited step instead of the first step
          if (
            storedStepsPath.includes(currentStepPath as StepsPath) &&
            currentStepPath !== StepsPath.Start
          ) {
            if (
              isUserAuthenticated() ||
              !AUTH_REQUIRED_PAGES.includes(currentStepPath as StepsPath)
            ) {
              // the pathname has been visited before
              // test case 1: any later visited step
              // target to the current step
              // do nothing
              return false;
            }
            // find the last in history that does not require auth
            let lastStep = popFromHistory() as StepsPath;
            while (AUTH_REQUIRED_PAGES.includes(lastStep)) {
              lastStep = popFromHistory() as StepsPath;
            }
            targetStepPath = lastStep;
          } else {
            // the pathname was not visited before
            // test case 1: first step; 2: any later not visited step
            // target to the last visited step
            targetStepPath = storedLastStepPath || StepsPath.Start;
          }
        } else {
          // the pathname is not a valid step
          // test case 1: any invalid step
          // target to the last visited step
          targetStepPath = storedLastStepPath || StepsPath.Start;
        }
      } else {
        // check if current step is the start step
        if (currentStepPath === StepsPath.Start) {
          // current step is last step already
          // do nothing
          return false;
        }
        // not visited before
        // test case 1: first step; 2: any later step; 3: any invalid step
        // target to the root of signup
        targetStepPath = StepsPath.Start;
      }
    } else {
      // global address id is not passed in
      // target to the marketing page
      router.push('/');
      return true;
    }

    const urlSearchParams = new URLSearchParams(searchParams?.toString() || '');
    const targetSignupUrl = buildSignupUrl(targetStepPath, urlSearchParams);

    // TODO: avoid true redirects using replace to keep steps history localy and avoid conflicts with browser history
    router.push(targetSignupUrl);
    // update local history
    addToHistory(targetStepPath as StepsPath);

    return true;
  }, [
    buyflowEnded,
    currentGlobalAddressId,
    currentStepPath,
    isUserAuthenticated,
    router,
    searchParams,
  ]);
};

/** Will attach singup prefix to specific step path and params at the end  */
export const buildSignupUrl = (stepPath: string, params: URLSearchParams) => {
  // The value of path passed in should always be one of StepsPath.
  const path = addPathPrefix(stepPath);
  return addSearchParams(path, params);
};

export interface SignOutParams {
  selectAccount?: boolean;
}

export const addSearchParams = (path: string, params: URLSearchParams) => {
  const hasParams = params.size > 0;
  if (!hasParams) {
    return path;
  }

  return `${path}?${params.toString()}`;
};

/**
 *
 * @param path - The path before transform
 * @returns The path transformed based on if the request come from google.com
 */
export const addPathPrefix = (path: string) => {
  const prefix = getPagePrefix();
  return path ? `${prefix}/${path}` : prefix;
};
